import { useEffect } from 'react'

import { selfApi } from '@/shared/services/api'
import { Dialog, DialogClose, DialogContent, DialogTitle } from '@/shared/ui/dialog'

import { AuthProvider, useAuthContext } from '../contexts/AuthContext'
import { ForgotPassword } from './ForgotPassword'
import { SignIn } from './SignIn'
import { SignUp } from './SignUp'

interface LandingAuthModalProps {
  modal: 'sign-in' | 'sign-up' | undefined
  onClose: () => void
}

function LandingAuthForms({ onClose }: { onClose: () => void }) {
  const { action } = useAuthContext()

  const forms = {
    'sign-in': SignIn,
    'sign-up': SignUp,
    'forgot-password': ForgotPassword
  } as const

  const Form = forms[action]
  return <Form onSuccess={onClose} />
}

function ModalActionSync({ modal }: { modal: 'sign-in' | 'sign-up' | undefined }) {
  const { setAction } = useAuthContext()

  useEffect(() => {
    if (modal) setAction(modal)
  }, [modal, setAction])

  return null
}

export function LandingAuthModal({ modal, onClose }: LandingAuthModalProps) {
  return (
    <AuthProvider
      config={{
        httpClient: selfApi,
        customNavigation: true,
        initialAction: modal ?? 'sign-in',
        afterSignInRedirectUrl: '/jornadas',
        disableReCaptcha: true,
        signIn: { loginLabel: 'E-mail', hideForgotPasswordAction: false },
        signUp: { withEmailConfirmation: true, skipConfirmation: true }
      }}
    >
      <ModalActionSync modal={modal} />
      <Dialog open={!!modal} onOpenChange={(open) => { if (!open) onClose() }}>
        <DialogContent
          className="p-0 gap-0 border-0 bg-transparent shadow-none sm:max-w-md"
          overlayClassName="overflow-visible max-h-none pt-[max(1rem,env(safe-area-inset-top))] pr-[max(1rem,env(safe-area-inset-right))] pb-[max(1rem,env(safe-area-inset-bottom))] pl-[max(1rem,env(safe-area-inset-left))]"
          hideCloseButton
        >
          <DialogTitle className="sr-only">Autenticação</DialogTitle>
          <DialogClose asChild>
            <button
              type="button"
              className="size-10 rounded-full bg-[linear-gradient(0deg,#C91D26_29%,#E0565D_100%)] shadow-[0_2px_0px_#6C1B1B,0_4px_4px_rgba(38,3,47,0.25),inset_0_4px_0px_#E24137] flex items-center justify-center absolute top-0 right-0 z-10 translate-x-1/2 -translate-y-1/2 outline-none"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width={20} height={19}>
                <path
                  fill="#D9D9D9"
                  d="M14.675.665a2.078 2.078 0 0 1 2.873 0l1.366 1.324a1.925 1.925 0 0 1 0 2.783l-4.687 4.541 4.687 4.541a1.925 1.925 0 0 1 0 2.783l-1.366 1.324a2.078 2.078 0 0 1-2.873 0L9.987 13.42 5.3 17.961a2.078 2.078 0 0 1-2.873 0L1.06 16.637a1.925 1.925 0 0 1 0-2.783l4.687-4.541L1.06 4.772a1.925 1.925 0 0 1 0-2.783L2.427.665a2.078 2.078 0 0 1 2.873 0l4.687 4.54 4.688-4.54Z"
                />
              </svg>
            </button>
          </DialogClose>
          <LandingAuthForms onClose={onClose} />
        </DialogContent>
      </Dialog>
    </AuthProvider>
  )
}
